import { useState } from 'react';
import { Box, ButtonBase, ClickAwayListener, makeStyles, Tooltip, Zoom } from '@material-ui/core';
import Vectors3 from '../../static/images/Vectors3.svg';
import MdroContact from '../../static/images/MdroContact.svg';
import MdroDetailLabel from '../../static/images/MdroDetailLabel.svg';
import Text from '../Text';

const useStylesLongremarkTooltip = makeStyles(() => ({
  arrow: {
    color: '#FFFFFF',
    '&::before': {
      boxShadow: '0 0 0.5rem 0 rgba(125, 134, 169, 0.2)',
    },
  },
  tooltip: ({ width, minWidth }) => ({
    borderRadius: '0.625rem',
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF',
    padding: '1rem',
    color: '#333',
    width,
    minWidth,
    maxWidth: 'calc(100vw - 2rem)',
    boxSizing: 'border-box',
  }),
}));

const useStyles = makeStyles(() => ({
  markiconBtn: {
    borderRadius: '0',
    padding: '0',
    marginLeft: '0.625rem',
    display: 'flex',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  title: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2E4469',
  },
  content: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    color: '#D81E06',
    '& strong': {
      fontFamily: 'NotoSans-Bold',
      fontWeight: 700,
    },
  },
  detailRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    minHeight: '1.875rem',
  },
  detailLabel: {
    position: 'relative',
    flexShrink: 0,
    width: 'max-content',
    minWidth: '4.75rem',
    height: '1.875rem',
  },
  detailLabelImage: {
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'block',
    width: '100%',
    height: '100%',
  },
  detailLabelText: ({ confirmedInd }) => ({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: 'max-content',
    minWidth: '4.75rem',
    height: '100%',
    padding: '0 0.875rem',
    color: confirmedInd === 1 ? '#FFFFFF' : '#7F6B06',
    fontFamily: 'NotoSans-Bold',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '1.25rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  }),
  detailMeta: {
    color: '#333',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: 500,
    lineHeight: '1.25',
    textAlign: 'right',
  },
  detailContent: ({ confirmedInd }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.625rem',
    padding: '0.75rem 1rem',
    borderRadius: '0.625rem',
    backgroundColor: confirmedInd === 1 ? '#fae5e5' : 'white',
    border: confirmedInd === 1 ? 'none' : '2px solid red',
  }),
  detailExpiry: {
    color: '#333',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    lineHeight: '1.5',
    textAlign: 'right',
  },
  background: ({ width = '5.875rem', height = '2.5rem', confirmedInd }) => ({
    backgroundImage: `url(${confirmedInd === 1 ? Vectors3 : MdroContact})`,
    backgroundSize: '100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    width,
    height,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  }),
}));

const Mdro = ({ popValue }) => {
  const classes = useStyles({ confirmedInd: popValue?.confirmedInd });
  const remark = popValue?.remark ?? '';
  const remarkParts = typeof remark === 'string' ? remark.split(/(\[[^\]]*\])/g) : [remark];
  const renderRemarkPart = (part, index) => {
    const isBold = typeof part === 'string' && part.startsWith('[') && part.endsWith(']');

    return (
      <span
        key={index}
        style={isBold ? { fontWeight: 'bold', fontFamily: 'NotoSans-bold', fontSize: '1rem' } : undefined}
      >
        {part}
      </span>
    );
  };

  function timestampToDate(timestamp) {
    if (!timestamp || Number.isNaN(new Date(timestamp).getTime())) {
      return '';
    }
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const monthIndex = date.getMonth(); // 获取月份索引（0-11）
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[monthIndex];
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day} ${monthName} ${year} ${hours}:${minutes}`;
  } // 时间戳转date

  return (
    <div className={classes.detailRoot}>
      <div className={classes.detailHeader}>
        <div className={classes.detailLabel}>
          <img
            className={classes.detailLabelImage}
            src={popValue?.confirmedInd === 1 ? MdroDetailLabel : MdroContact}
            alt=""
          />
          <span className={classes.detailLabelText}>{popValue?.alertDesc}</span>
        </div>
        <div className={classes.detailMeta}>
          <div>Alert since {timestampToDate(popValue?.effectiveFrom)}</div>
          <Box mt="0.275rem">Created by: {popValue?.createHospCd}</Box>
        </div>
      </div>
      {(remark || popValue?.effectiveTo) && (
        <div className={classes.detailContent}>
          {remark && <div className={classes.content}>{remarkParts.map(renderRemarkPart)}</div>}
          {popValue?.effectiveTo && <div className={classes.detailExpiry}>{timestampToDate(popValue.effectiveTo)}</div>}
        </div>
      )}
    </div>
  );
};

export const MdroIcon = ({ onClick, value, confirmedInd, width, height, fontSize, type = 'admission' }) => {
  const classes = useStyles({ width, height, confirmedInd });
  const contactColor = type === 'admission' ? '#7F6B06' : '#D81E06';
  return (
    <Box className={classes.background} onClick={onClick}>
      <Text fontSize={fontSize} color={confirmedInd === 1 ? '#FFFFFF' : contactColor}>
        {value.slice(0, 4)}
      </Text>
    </Box>
  );
};

const MdroInfo = ({ minWidth, width = '21.5rem', placement, id = 'hover', content, popValue }) => {
  const longremarkclasses = useStylesLongremarkTooltip({ width, minWidth });
  const [toolTipOpen, setToolTipOpen] = useState(false);

  const handleTooltipClose = (event) => {
    event.stopPropagation();
    setToolTipOpen(false);
  };

  const handleTooltipOpen = (event) => {
    event.stopPropagation();
    setToolTipOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        id={id}
        classes={longremarkclasses}
        title={<Mdro popValue={popValue} />}
        arrow
        TransitionComponent={Zoom}
        disableFocusListener
        disableHoverListener
        disableTouchListener
        onClose={handleTooltipClose}
        open={toolTipOpen}
      >
        <ButtonBase onClick={handleTooltipOpen}>
          <MdroIcon
            value={content}
            confirmedInd={popValue?.confirmedInd}
            fontSize="0.75rem"
            width="4rem"
            height="2rem"
          />
        </ButtonBase>
      </Tooltip>
    </ClickAwayListener>
  );
};

export default MdroInfo;
